import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import { MessagesSquare, Target, Flame, ArrowRight, Clock, Zap, Infinity as InfinityIcon, Sparkles } from "lucide-react";

const PLAN_META = {
    free: { label: "Free", price: "$0", next: "standard" },
    standard: { label: "Standard", price: "$19", next: "pro" },
    pro: { label: "Pro", price: "$49", next: "enterprise" },
    enterprise: { label: "Enterprise", price: "Custom", next: null },
};

const Stat = ({ label, value, icon: Icon, testid }) => (
    <div className="border border-zinc-900 p-6 bg-[#0c0c0c]" data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
            <Icon className="w-4 h-4 text-signal" strokeWidth={1.5} />
        </div>
        <div className="font-heading font-black text-4xl">{value}</div>
    </div>
);

export default function Dashboard() {
    const { user, authHeaders } = useAuth();
    const [convs, setConvs] = useState([]);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [convRes, planRes] = await Promise.all([
                    axios.get(`${API}/conversations`, { headers: authHeaders() }),
                    axios.get(`${API}/billing/plan`, { headers: authHeaders() }),
                ]);
                setConvs(convRes.data);
                setPlan(planRes.data);
            } catch (e) { /* noop */ }
            finally { setLoading(false); }
        })();
    }, [authHeaders]);

    const totalReplies = convs.reduce((sum, c) => sum + (c.variants?.length || 0), 0);
    const avgConfidence = convs.length
        ? Math.round(
            convs.flatMap((c) => c.variants || []).reduce((s, v) => s + (v.confidence || 0), 0) /
            Math.max(1, convs.flatMap((c) => c.variants || []).length)
        )
        : 0;

    return (
        <div data-testid="dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-signal mb-2">/ COCKPIT</div>
                    <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">
                        Hey, {user?.name?.split(" ")[0] || "there"}.
                    </h1>
                    <p className="text-zinc-400 mt-2">Pick up where you left off, or draft the next reply.</p>
                </div>
                <Link
                    to="/app/new"
                    data-testid="dashboard-new-btn"
                    className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-5 py-3 rounded-md font-semibold transition-colors"
                >
                    New reply <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat label="Conversations" value={convs.length} icon={MessagesSquare} testid="stat-conversations" />
                <Stat label="Replies drafted" value={totalReplies} icon={Flame} testid="stat-replies" />
                <Stat label="Avg. confidence" value={`${avgConfidence}%`} icon={Target} testid="stat-confidence" />
            </div>

            {/* Plan & usage widget */}
            {plan && (() => {
                const tierKey = plan.admin_unlimited ? "pro" : (plan.tier || (plan.plan === "free" ? "free" : "pro"));
                const meta = PLAN_META[tierKey] || PLAN_META.free;
                const used = plan.generations_this_period || 0;
                const limit = plan.generation_limit; // null = unlimited
                const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                const nearCap = limit && pct >= 80;
                const isUnlimited = !limit;
                const nextPlan = meta.next ? PLAN_META[meta.next] : null;

                return (
                    <div className="border border-zinc-900 bg-[#0c0c0c] p-6 mb-10" data-testid="dashboard-plan-card">
                        <div className="flex flex-wrap items-start justify-between gap-6">
                            <div className="flex-1 min-w-[220px]">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Your plan</div>
                                    {plan.admin_unlimited && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono bg-signal/10 text-signal border border-signal/30 uppercase tracking-widest">
                                            <InfinityIcon className="w-2.5 h-2.5" /> Admin
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-baseline gap-2 mb-3" data-testid="dashboard-plan-label">
                                    <span className="font-heading font-black text-3xl capitalize">{meta.label}</span>
                                    <span className="text-sm text-zinc-500 font-mono">{meta.price}{meta.price !== "Custom" ? "/mo" : ""}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-zinc-300 mb-2" data-testid="dashboard-usage-text">
                                    <Zap className="w-3.5 h-3.5 text-signal" />
                                    {isUnlimited ? (
                                        <span>Unlimited generations this period</span>
                                    ) : (
                                        <span>
                                            <span className="text-white font-semibold">{used.toLocaleString()}</span>
                                            <span className="text-zinc-500"> / {limit.toLocaleString()} generations this month</span>
                                        </span>
                                    )}
                                </div>
                                {!isUnlimited && (
                                    <div className="w-full h-1.5 bg-zinc-900 rounded-full overflow-hidden" data-testid="dashboard-usage-bar">
                                        <div
                                            className={`h-full transition-all ${pct >= 100 ? "bg-red-500" : nearCap ? "bg-amber-400" : "bg-signal"}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                )}
                                {nearCap && (
                                    <p className="text-xs text-amber-400/90 mt-2 font-mono uppercase tracking-widest" data-testid="dashboard-near-cap">
                                        · {pct}% used — consider upgrading
                                    </p>
                                )}
                            </div>

                            {!plan.admin_unlimited && nextPlan && (
                                <Link
                                    to="/app/billing"
                                    data-testid="dashboard-upgrade-btn"
                                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm transition-colors ${
                                        nearCap
                                            ? "bg-signal hover:bg-signal-hover text-white"
                                            : "border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white"
                                    }`}
                                >
                                    <Sparkles className="w-4 h-4" />
                                    {nearCap ? `Upgrade to ${nextPlan.label}` : `See all 4 plans`}
                                </Link>
                            )}
                            {plan.admin_unlimited && (
                                <Link
                                    to="/app/billing"
                                    data-testid="dashboard-upgrade-btn"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-sm border border-zinc-700 hover:border-signal hover:bg-signal/10 text-white transition-colors"
                                >
                                    <Sparkles className="w-4 h-4" />
                                    View plans
                                </Link>
                            )}
                        </div>
                    </div>
                );
            })()}

            <div className="border border-zinc-900 bg-[#0c0c0c]">
                <div className="p-6 border-b border-zinc-900 flex items-center justify-between">
                    <h2 className="font-heading font-bold text-xl">Recent conversations</h2>
                    <Link to="/app/history" className="text-sm text-zinc-400 hover:text-white" data-testid="dashboard-view-all">
                        View all →
                    </Link>
                </div>
                {loading ? (
                    <div className="p-12 text-center text-zinc-500">Loading…</div>
                ) : convs.length === 0 ? (
                    <div className="p-12 text-center">
                        <Clock className="w-8 h-8 text-zinc-700 mx-auto mb-4" strokeWidth={1.5} />
                        <p className="text-zinc-400 mb-4">No conversations yet. Let's draft your first reply.</p>
                        <Link
                            to="/app/new"
                            className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white px-5 py-2.5 rounded-md font-semibold text-sm"
                            data-testid="dashboard-empty-cta"
                        >
                            Start a conversation <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="divide-y divide-zinc-900">
                        {convs.slice(0, 5).map((c) => (
                            <Link
                                key={c.id}
                                to={`/app/history`}
                                state={{ focusId: c.id }}
                                className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-6 hover:bg-zinc-900/30 transition-colors"
                                data-testid={`dashboard-conv-${c.id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1 text-xs font-mono uppercase tracking-widest text-zinc-500">
                                        <span>{c.platform}</span>
                                        <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                                        <span>{c.tone}</span>
                                        <span className="w-1 h-1 bg-zinc-700 rounded-full" />
                                        <span>{c.industry}</span>
                                    </div>
                                    <div className="text-sm text-zinc-300 truncate">{c.conversation.slice(0, 140)}…</div>
                                </div>
                                <div className="text-xs text-zinc-500 font-mono shrink-0">
                                    {new Date(c.created_at).toLocaleString()}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
